import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Upload, X, Info, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs

const GENRES = [
  'Romance', 'Romance Fantasy', 'Drama', 'Action', 'Action Fantasy',
  'Thriller/Horror', 'Mystery', 'Sci-Fi', 'Fantasy', 'Slice of Life',
  'Comedy', 'BL', 'GL', 'LGBTQ+', 'Non-Fiction', 'Historical',
  'Urban Fantasy', 'Dystopian', 'Young Adult', 'Literary Fiction'
];

const CONTENT_RATINGS = [
  { value: 'G', label: 'G - General Audiences' },
  { value: 'PG', label: 'PG - Parental Guidance' },
  { value: 'PG-13', label: 'PG-13 - Parents Strongly Cautioned' },
  { value: 'R', label: 'R - Restricted' },
  { value: 'NC-17', label: 'NC-17 - Adults Only' }
];

const CreateNovel = () => {
  const { user, loading, updateUser } = useAuth();
  const navigate = useNavigate();
  const autoSaveTimeout = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    thumbnail: null,
    cover: null,
    banner: null,
    bannerLink: '',
    adBanner: null,
    adLink: '',
    genres: [],
    mainGenre: '',
    tags: [],
    descendingOrder: false,
    readingFormat: 'vertical_scroll',
    contentRating: 'PG-13',
    ratingDetails: {}
  });

  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [adBannerPreview, setAdBannerPreview] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [loading, user, navigate]);

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && !formData.slug) {
      const generated = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 100);
      setFormData(prev => ({ ...prev, slug: generated }));
    }
  }, [formData.title]);

  // Check slug availability
  useEffect(() => {
    if (formData.slug) {
      const timeout = setTimeout(async () => {
        setCheckingSlug(true);
        try {
          const response = await axios.get('/api/novels?search=${formData.slug}');
          const exists = response.data.novels.some(n => n.slug === formData.slug);
          setSlugAvailable(!exists);
        } catch (error) {
          console.error('Error checking slug:', error);
        } finally {
          setCheckingSlug(false);
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [formData.slug]);

  // Auto-save draft
  useEffect(() => {
    if (autoSaveTimeout.current) {
      clearTimeout(autoSaveTimeout.current);
    }

    if (formData.title || formData.description) {
      autoSaveTimeout.current = setTimeout(() => {
        saveDraft();
      }, 10000);
    }

    return () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    };
  }, [formData]);

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload JPG or PNG files only');
      return;
    }

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 20MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to upload images');
        setUploading(false);
        return;
      }

      const response = await axios.post('/api/upload', formData, {
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let axios set it with boundary
        }
      });

      const imageUrl = response.data.url;

      // Update form data
      setFormData(prev => ({ ...prev, [type]: imageUrl }));

      // Set preview
      const reader = new FileReader();
      reader.onloadend = () => {
        switch(type) {
          case 'thumbnail':
            setThumbnailPreview(reader.result);
            break;
          case 'cover':
            setCoverPreview(reader.result);
            break;
          case 'banner':
            setBannerPreview(reader.result);
            break;
          case 'adBanner':
            setAdBannerPreview(reader.result);
            break;
        }
      };
      reader.readAsDataURL(file);

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload image';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleGenreToggle = (genre) => {
    if (formData.genres.includes(genre)) {
      setFormData(prev => ({
        ...prev,
        genres: prev.genres.filter(g => g !== genre)
      }));
    } else if (formData.genres.length < 3) {
      setFormData(prev => ({
        ...prev,
        genres: [...prev.genres, genre]
      }));
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (formData.tags.length < 10) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const saveDraft = async () => {
    if (!formData.title && !formData.description) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/novels/draft', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const isFormValid = () => {
    return (
      formData.title.trim() &&
      formData.description.trim() &&
      formData.thumbnail &&
      formData.cover &&
      formData.mainGenre &&
      slugAvailable
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/novels',
        {
          title: formData.title,
          cover_url: formData.cover,
          genres: formData.genres.length > 0 ? formData.genres : [formData.mainGenre],
          tags: formData.tags,
          description: formData.description,
          reading_format: formData.readingFormat,
          content_rating: formData.contentRating,
          rating_details: formData.ratingDetails
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh user data in case role was upgraded
      try {
        const userResponse = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userResponse.data.role !== user.role) {
          const updatedUser = { ...user, role: userResponse.data.role };
          updateUser(updatedUser);
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }

      toast.success('Novel series created successfully!');
      navigate(`/novel/${response.data.id}`);
    } catch (error) {
      console.error('Error creating novel:', error);
      toast.error(error.response?.data?.detail || 'Failed to create novel series');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-16 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-white">Create New Novel Series</h1>
            <div className="flex space-x-3">
              <button
                onClick={saveDraft}
                disabled={saving}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Save Draft
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid() || saving}
                className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
              >
                {saving ? 'Creating...' : 'Create Series'}
              </button>
            </div>
          </div>
          <p className="text-gray-400">Set up your story before uploading your first chapter.</p>
        </div>

        {/* Title */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <label className="block text-white font-semibold mb-2">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value.substring(0, 100) }))}
            placeholder="Enter your series title"
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            maxLength={100}
          />
          <p className="text-xs text-gray-500 mt-1">{formData.title.length}/100</p>
        </div>

        {/* URL Slug */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <label className="block text-white font-semibold mb-2">
            URL Slug
          </label>
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-gray-400">trapcomix.com/novel/</span>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 100) }))}
              className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              maxLength={100}
            />
          </div>
          {checkingSlug ? (
            <p className="text-xs text-gray-400">Checking availability...</p>
          ) : slugAvailable ? (
            <p className="text-xs text-green-500 flex items-center"><Check className="w-3 h-3 mr-1" /> Available</p>
          ) : (
            <p className="text-xs text-red-500">This slug is already taken</p>
          )}
        </div>

        {/* Description */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <label className="block text-white font-semibold mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value.substring(0, 2000) }))}
            placeholder="Describe your story..."
            rows={6}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            maxLength={2000}
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-gray-500">This appears on your series page.</p>
            <p className="text-xs text-gray-500">{formData.description.length}/2000</p>
          </div>
        </div>

        {/* Artwork Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Artwork & Media</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Thumbnail */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Thumbnail (300×300) <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center hover:border-green-500 transition-colors cursor-pointer">
                {thumbnailPreview ? (
                  <div className="relative">
                    <img src={thumbnailPreview} alt="Thumbnail" className="w-full h-48 object-cover rounded" />
                    <button
                      onClick={() => {
                        setThumbnailPreview(null);
                        setFormData(prev => ({ ...prev, thumbnail: null }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400">Click to upload</p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="sr-only"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'thumbnail')}
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Appears in search results and listings</p>
            </div>

            {/* Book Cover */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Book Cover (960×1440) <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-4 text-center hover:border-green-500 transition-colors cursor-pointer">
                {coverPreview ? (
                  <div className="relative">
                    <img src={coverPreview} alt="Cover" className="w-full h-48 object-cover rounded" />
                    <button
                      onClick={() => {
                        setCoverPreview(null);
                        setFormData(prev => ({ ...prev, cover: null }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400">Click to upload</p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      className="sr-only"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'cover')}
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Series title must be visible</p>
            </div>
          </div>
        </div>

        {/* Genres */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <label className="block text-white font-semibold mb-2">
            Genres (Select up to 3)
          </label>
          <div className="flex flex-wrap gap-2 mb-4">
            {GENRES.map(genre => (
              <button
                key={genre}
                onClick={() => handleGenreToggle(genre)}
                disabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  formData.genres.includes(genre)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {genre}
              </button>
            ))}
          </div>

          <label className="block text-white font-semibold mb-2 mt-4">
            Main Genre <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.mainGenre}
            onChange={(e) => setFormData(prev => ({ ...prev, mainGenre: e.target.value }))}
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Select main genre</option>
            {GENRES.map(genre => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <label className="block text-white font-semibold mb-2">
            Tags (Max 10)
          </label>
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Type and press Enter to add tags"
            className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 mb-3"
            disabled={formData.tags.length >= 10}
          />
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag, index) => (
              <span key={index} className="bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full flex items-center space-x-2">
                <span>{tag}</span>
                <button onClick={() => handleRemoveTag(index)} className="hover:text-purple-300">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">{formData.tags.length}/10 tags</p>
        </div>

        {/* Episode Order */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-white font-semibold">Descending Order</label>
              <p className="text-sm text-gray-400">Start readers at most recent episode (for short-form series)</p>
            </div>
            <label className="relative inline-block w-12 h-6">
              <input
                type="checkbox"
                checked={formData.descendingOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, descendingOrder: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-12 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 rounded-full peer peer-checked:after:translate-x-6 peer-checked:bg-green-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CreateNovel;